import * as React from 'react'
import { healthCheck } from '../../services/CommonServices/httpService'
import { markHealth } from '../../services/CommonServices/healthGate'

type ConnectionStatus = 'online' | 'offline' | 'limited'

interface ConnectionContextType {
	connectionStatus: ConnectionStatus
}

const ConnectionContext = React.createContext<ConnectionContextType | undefined>(undefined)

export const useConnectionContext = () => {
	const context = React.useContext(ConnectionContext)
	if (context === undefined) {
		throw new Error('useConnection must be used within a ConnectionProvider')
	}
	return context
}

interface ConnectionProviderProps {
	children: React.ReactNode
}

export const ConnectionProvider: React.FC<ConnectionProviderProps> = ({ children }) => {
	const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>('limited')

	// Connection status monitoring
	React.useEffect(() => {
		const handleOnline = async () => {
			await testConnection();
		}

		const handleOffline = () => {
			setConnectionStatus('offline')
			markHealth(false)
		}

		const testConnection = async () => {
			try {
				const response = await healthCheck()
				setConnectionStatus(response ? 'online' : 'limited')
				markHealth(response)
			} catch (error) {
				setConnectionStatus('offline')
				markHealth(false)
			}
		}

		testConnection()

		window.addEventListener('online', handleOnline)
		window.addEventListener('offline', handleOffline)

		// Periodic connection test (every 30 seconds)
		const interval = setInterval(testConnection, 30000)

		return () => {
			window.removeEventListener('online', handleOnline)
			window.removeEventListener('offline', handleOffline)
			clearInterval(interval)
		}
	}, [])

	React.useEffect(() => {
		localStorage.setItem('connectionStatus', connectionStatus)
	}, [connectionStatus])

	const value: ConnectionContextType = {
		connectionStatus,
	}

	return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>
}
